import { Component, type ReactNode } from 'react';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import WebBootHelper from 'src/presentation/helpers/WebBootHelper';

interface LazyScreenBoundaryProps {
	children: ReactNode;
	onRetry: () => void;
}

interface LazyScreenBoundaryState {
	error: Error | null;
	hasRetried: boolean;
}

// A rejected lazy import is thrown to the nearest boundary, not to the Suspense around it.
class LazyScreenBoundary extends Component<LazyScreenBoundaryProps, LazyScreenBoundaryState> {
	static getDerivedStateFromError(error: Error): Pick<LazyScreenBoundaryState, 'error'> {
		return { error };
	}

	override state: LazyScreenBoundaryState = { error: null, hasRetried: false };

	override componentDidCatch(error: Error): void {
		if (WebBootHelper.isChunkLoadError(error)) {
			useServicesStore.getState().captureEvent({ message: 'Loading a lazy screen failed.', extra: { error } });
		}
	}

	retry = () => {
		this.setState({ error: null, hasRetried: true });
		this.props.onRetry();
	};

	signOut = () => {
		useAuthStore
			.getState()
			.logout()
			.catch((error: unknown) =>
				useServicesStore
					.getState()
					.captureEvent({ message: 'Signing out after a failed screen load failed.', extra: { error } }),
			);
	};

	override render() {
		const { error, hasRetried } = this.state;

		// Every signed-in screen renders below this boundary, and only a failed load is its to handle.
		if (error && !WebBootHelper.isChunkLoadError(error)) {
			throw error;
		}

		// A screen that keeps failing would otherwise leave a signed-in member no way out.
		return error ? (
			<ErrorView onPress={this.retry} onSignOutPress={hasRetried ? this.signOut : undefined} />
		) : (
			this.props.children
		);
	}
}

export default LazyScreenBoundary;
